import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, Clock, Users } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/currencyFormatter";
import { createReservation } from "@/services/reservations/reservationService";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface CreateReservationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  onSuccess: () => void;
}

interface ExistingReservation {
  table_id: string | null;
  reservation_date: string;
  reservation_time: string;
}

const isTimeSlotConflicting = (
  existingTime: string,
  newTime: string,
  bufferMinutes: number = 90
) => {
  const existing = new Date(`1970-01-01T${existingTime}`);
  const new_ = new Date(`1970-01-01T${newTime}`);
  const diffInMinutes = Math.abs(
    (existing.getTime() - new_.getTime()) / (1000 * 60)
  );
  return diffInMinutes < bufferMinutes;
};

export function CreateReservationDialog({
  open,
  onOpenChange,
  storeId,
  onSuccess,
}: CreateReservationDialogProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [tables, setTables] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    reservation_date: undefined as Date | undefined,
    reservation_time: "",
    party_size: 1,
    table_id: "",
    special_requests: "",
  });
  const [existingReservations, setExistingReservations] = useState<
    ExistingReservation[]
  >([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);

  useEffect(() => {
    if (open && storeId) {
      fetchTables();
    }
  }, [open, storeId]);

  const fetchTables = async () => {
    try {
      const { data, error } = await supabase
        .from("store_tables")
        .select("*")
        .eq("store_id", storeId)
        .order("number");

      if (error) {
        console.error("Error fetching tables:", error);
        return;
      }

      setTables(data || []);
    } catch (error) {
      console.error("Error fetching tables:", error);
    }
  };

  const fetchExistingReservations = async (date?: Date) => {
    if (!date) return;

    try {
      const { data, error } = await supabase
        .from("reservations")
        .select("*")
        .eq("store_id", storeId)
        .eq("reservation_date", format(date, "yyyy-MM-dd"))
        .in("status", ["confirmed", "pending"]);

      if (error) throw error;

      setExistingReservations(data || []);
      updateAvailableTimeSlots(date, data || []);
    } catch (error) {
      console.error("Error fetching reservations:", error);
      toast({
        title: "Error",
        description: "Failed to fetch existing reservations",
        variant: "destructive",
      });
    }
  };

  const updateAvailableTimeSlots = (
    date: Date,
    reservations: ExistingReservation[]
  ) => {
    const baseTimeSlots = [
      "09:00",
      "09:30",
      "10:00",
      "10:30",
      "11:00",
      "11:30",
      "12:00",
      "12:30",
      "13:00",
      "13:30",
      "14:00",
      "14:30",
      "15:00",
      "15:30",
      "16:00",
      "16:30",
      "17:00",
      "17:30",
      "18:00",
      "18:30",
      "19:00",
      "19:30",
      "20:00",
      "20:30",
      "21:00",
      "21:30",
      "22:00",
    ];

    const now = new Date();
    const isToday = format(date, "yyyy-MM-dd") === format(now, "yyyy-MM-dd");

    let availableSlots = baseTimeSlots;

    if (isToday) {
      const currentTime = format(now, "HH:mm");
      availableSlots = availableSlots.filter((slot) => slot > currentTime);
    }

    availableSlots = availableSlots.filter((slot) => {
      return !reservations.some((res) =>
        isTimeSlotConflicting(res.reservation_time.slice(0, 5), slot)
      );
    });

    setAvailableTimeSlots(availableSlots);

    if (
      formData.reservation_time &&
      !availableSlots.includes(formData.reservation_time)
    ) {
      setFormData((prev) => ({ ...prev, reservation_time: "" }));
    }
  };

  useEffect(() => {
    if (formData.reservation_date) {
      fetchExistingReservations(formData.reservation_date);
    }
  }, [formData.reservation_date, formData.table_id]);

  const handleTableSelection = (tableId: string) => {
    const selectedTableId = tableId === "no-preference" ? "" : tableId;
    setFormData((prev) => ({
      ...prev,
      table_id: selectedTableId,
    }));

    if (formData.reservation_date) {
      updateAvailableTimeSlots(formData.reservation_date, existingReservations);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.reservation_date || !formData.reservation_time) {
      toast({
        title: "Missing Information",
        description: "Please select both date and time",
        variant: "destructive",
      });
      return;
    }

    const conflictingReservation = existingReservations.find((res) => {
      const sameTable = res.table_id === formData.table_id;
      const sameDate =
        res.reservation_date ===
        format(formData.reservation_date, "yyyy-MM-dd");
      const timeConflict = isTimeSlotConflicting(
        res.reservation_time,
        formData.reservation_time
      );
      return sameTable && sameDate && timeConflict;
    });

    if (conflictingReservation) {
      toast({
        title: "Table Unavailable",
        description:
          "This table is already booked for the selected time slot or too close to another reservation",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const reservation = await createReservation({
        store_id: storeId,
        table_id: formData.table_id || null,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        reservation_date: format(formData.reservation_date, "yyyy-MM-dd"),
        reservation_time: formData.reservation_time,
        party_size: formData.party_size,
        special_requests: formData.special_requests,
        status: "confirmed",
      });

      if (reservation) {
        toast({
          title: "Success",
          description: "Reservation created successfully",
        });
        onSuccess();
        onOpenChange(false);
        setFormData({
          customer_name: "",
          customer_email: "",
          customer_phone: "",
          reservation_date: undefined,
          reservation_time: "",
          party_size: 1,
          table_id: "",
          special_requests: "",
        });
      } else {
        throw new Error("Failed to create reservation");
      }
    } catch (error) {
      console.error("Error creating reservation:", error);
      toast({
        title: "Error",
        description: "Failed to create reservation",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t("reservation.title")}
          </DialogTitle>
          <DialogDescription>{t("reservation.description")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customer_name">
                {t("reservation.customerName")} *
              </Label>
              <Input
                id="customer_name"
                value={formData.customer_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    customer_name: e.target.value,
                  }))
                }
                placeholder={t("reservation.customerName")}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer_email">Email Address *</Label>
              <Input
                id="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    customer_email: e.target.value,
                  }))
                }
                placeholder="customer@example.com"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer_phone">Phone Number</Label>
              <Input
                id="customer_phone"
                type="tel"
                value={formData.customer_phone}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    customer_phone: e.target.value,
                  }))
                }
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="party_size">Party Size</Label>
              <Select
                value={formData.party_size.toString()}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    party_size: parseInt(value),
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select party size" />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(12)].map((_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {i + 1}{" "}
                      {i === 0
                        ? t("reservation.guest")
                        : t("reservation.guests")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Reservation Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.reservation_date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.reservation_date ? (
                      format(formData.reservation_date, "PPP")
                    ) : (
                      <span>{t("reservation.pickDate")}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.reservation_date}
                    onSelect={(date) =>
                      setFormData((prev) => ({
                        ...prev,
                        reservation_date: date,
                      }))
                    }
                    disabled={(date) => date < new Date()}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reservation_time">Time *</Label>
              <Select
                value={formData.reservation_time}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, reservation_time: value }))
                }
              >
                <SelectTrigger>
                  <Clock className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {availableTimeSlots.map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableTimeSlots.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {t("reservation.noTimeSlots")}
                </p>
              )}
            </div>
          </div>

          {tables.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="table_id">Preferred Table (Optional)</Label>
              <Select
                value={formData.table_id || "no-preference"}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    table_id: value === "no-preference" ? "" : value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a table (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-preference">
                    {t("reservation.noPreference")}
                  </SelectItem>
                  {tables.map((table) => (
                    <SelectItem key={table.id} value={table.id}>
                      {t("reservation.table")} {table.number} - {table.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="special_requests">Special Requests</Label>
            <Textarea
              id="special_requests"
              value={formData.special_requests}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  special_requests: e.target.value,
                }))
              }
              placeholder={t("reservation.specialRequestsPlaceholder")}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("reservation.cancel")}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t("reservation.creating") : t("reservation.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
